'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import posthog from 'posthog-js';
import { useAppVersionStore } from '@/lib/stores/app-version.store';
import { isReloadUnsafe } from './reload-safety';
import { BUILD_SHA } from '@/lib/version';

/**
 * Keeps long-lived tabs on the current deployment.
 *
 * Vercel Skew Protection (`skew_protection = "7 days"` in `infra/vercel/project.tf`) pins
 * every request a loaded page makes to the deployment it was served from. That's what
 * stops chunk 404s mid-deploy, but it also disables Next's own build-mismatch hard
 * navigation, because the client never sees a mismatch to react to — so a provider who
 * only ever navigates client-side keeps running a week-old bundle. This is the
 * counterpart to pinning, not a workaround for it.
 *
 * Detect by polling; apply at the next route change, where a full load costs the user
 * nothing and loses no state. Never reload under someone sitting still — for them the
 * dashboard nav offers `UpdateAvailableButton`.
 *
 * Renders nothing; only sets the flag in `app-version.store`.
 */

// How often to ask, while the tab is in the foreground. Deploys are not frequent enough
// to justify tighter, and a hidden tab is never polled at all.
const POLL_INTERVAL_MS = 5 * 60 * 1000;
// Floor between checks, so a burst of tab switches doesn't turn into a burst of requests.
const MIN_CHECK_GAP_MS = 60 * 1000;

export function VersionWatcher() {
  const pathname = usePathname();
  const updateAvailable = useAppVersionStore((s) => s.updateAvailable);
  const markUpdateAvailable = useAppVersionStore((s) => s.markUpdateAvailable);
  const lastCheckedAt = useRef(0);
  const mountedPath = useRef(pathname);
  const reloading = useRef(false);
  // Whether the route change being handled was a Back/Forward traversal —
  // set by the browser's popstate, consumed by the apply effect below.
  const lastNavWasPop = useRef(false);

  useEffect(() => {
    const onPop = () => {
      lastNavWasPop.current = true;
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Detect. Skipped entirely when there's no build sha to compare (local dev).
  useEffect(() => {
    if (!BUILD_SHA || updateAvailable) return;

    const check = async () => {
      const now = Date.now();
      if (now - lastCheckedAt.current < MIN_CHECK_GAP_MS) return;
      lastCheckedAt.current = now;

      try {
        // `credentials: 'omit'` is load-bearing: it drops the `__vdpl` cookie that pins
        // requests to this tab's deployment. Without it the endpoint answers from our own
        // stale deployment and always agrees with us.
        const res = await fetch('/api/version', {
          cache: 'no-store',
          credentials: 'omit',
          headers: { accept: 'application/json' },
        });
        if (!res.ok) return;
        const { sha } = (await res.json()) as { sha?: string };
        if (sha && sha !== BUILD_SHA) markUpdateAvailable();
      } catch {
        // Offline, or the request was cut off by a navigation. Nothing to do — the next
        // poll or the next re-focus asks again.
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };

    const interval = setInterval(onVisible, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [updateAvailable, markUpdateAvailable]);

  // Apply at the first FORWARD route change after a new version appears. By this point
  // Next has already client-rendered the destination with the old bundle; replacing it
  // with a real load of the same URL is a brief flash and lands the user on the new
  // build. Never under a Back/Forward traversal: the user asked to go back, and a
  // document swap under that press reads as the Back button misfiring — the reload
  // simply waits for their next forward navigation.
  useEffect(() => {
    if (pathname === mountedPath.current) return;
    // Consume the traversal flag on every route change, armed or not.
    const wasPop = lastNavWasPop.current;
    lastNavWasPop.current = false;
    if (!updateAvailable || reloading.current) return;
    if (isReloadUnsafe()) return;
    if (wasPop) {
      mountedPath.current = pathname;
      return;
    }

    reloading.current = true;
    posthog.capture('app_version_reloaded', { from: BUILD_SHA, trigger: 'navigation' });
    window.location.href = `${window.location.pathname}${window.location.search}`;
  }, [pathname, updateAvailable]);

  return null;
}
